import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import React, { useState } from 'react';
import AddressAutocompleteField from '../../../components/AddressAutocompleteField';
import type { Holder } from '../../data/types';
import { date, dateTime } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useToast } from '../../ui/feedback';
import { KeyValueGrid, SectionCard } from '../../ui/primitives';

function EditContactDialog({
  holder,
  open,
  onClose,
}: {
  holder: Holder;
  open: boolean;
  onClose: () => void;
}) {
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [email, setEmail] = useState(holder.email);
  const [phone, setPhone] = useState(holder.phone);
  const [address, setAddress] = useState(holder.address);
  const [reason, setReason] = useState('');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Edit contact details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <TextField
            label="Phone"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
          <AddressAutocompleteField
            label="Street address"
            value={address.line1}
            onChange={(line1) => setAddress((current) => ({ ...current, line1 }))}
            onPlaceParts={(parts) => setAddress((current) => ({ ...current, ...parts }))}
          />
          <TextField
            label="Reason for change (recorded in audit trail)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!reason.trim()}
          onClick={() => {
            dispatch({
              type: 'HOLDER_UPDATE',
              id: holder.id,
              patch: { email, phone, address },
              reason,
            });
            toast('Contact details updated and audited');
            onClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function HolderOverviewTab({ holder }: { holder: Holder }) {
  const { state } = useRegistry();
  const [editing, setEditing] = useState(false);
  const login = state.investorLogins.find((item) => item.id === holder.investorLoginId);
  const address = holder.address;
  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
      <SectionCard
        title="Registered details"
        action={
          <Button size="small" onClick={() => setEditing(true)}>
            Edit
          </Button>
        }
      >
        <KeyValueGrid
          items={[
            { label: 'Registered name', value: holder.name },
            { label: 'Account number', value: holder.accountNumber },
            { label: 'Primary contact', value: holder.contactName },
            { label: 'Email', value: holder.email },
            { label: 'Phone', value: holder.phone },
            {
              label: 'Registered address',
              value: `${address.line1}, ${address.suburb} ${address.state} ${address.postcode}, ${address.country}`,
            },
            { label: 'ABN', value: holder.abn ?? '—' },
            { label: 'ACN', value: holder.acn ?? '—' },
            {
              label: 'Communication preference',
              value: holder.communication === 'EMAIL' ? 'Email and app' : 'Post',
            },
            { label: 'Adviser / accountant', value: holder.adviser ?? '—' },
          ]}
        />
      </SectionCard>
      <SectionCard title="Investor app access">
        {login ? (
          <KeyValueGrid
            items={[
              { label: 'Login', value: `${login.name} (${login.email})` },
              { label: 'MFA', value: login.mfaEnabled ? 'Enabled' : 'Not enabled' },
              { label: 'Last login', value: dateTime(login.lastLogin) },
              { label: 'Entities under this login', value: login.holderIds.length },
              {
                label: 'Linked in core platform',
                value: 'Contact + investment entity (external IDs)',
              },
              { label: 'Notifications', value: 'Push, email' },
            ]}
          />
        ) : (
          <KeyValueGrid
            items={[
              { label: 'Login', value: 'No investor app login' },
              {
                label: 'Statements delivered by',
                value: holder.communication === 'EMAIL' ? 'Email' : 'Post',
              },
              { label: 'Invite', value: 'Send an app invitation from Contacts (existing feature)' },
            ]}
          />
        )}
      </SectionCard>
      <SectionCard title="Registry record">
        <KeyValueGrid
          items={[
            { label: 'Created', value: date(holder.createdAt) },
            { label: 'Holder type', value: holder.type },
            {
              label: 'Tenant',
              value: state.tenants.find((tenant) => tenant.id === holder.tenantId)?.name,
            },
            { label: 'Record ID', value: holder.id },
          ]}
        />
      </SectionCard>
      <EditContactDialog
        key={editing ? 'open' : 'closed'}
        holder={holder}
        open={editing}
        onClose={() => setEditing(false)}
      />
    </Box>
  );
}
